import React from 'react'
import EChartCustomized from '@/components/EchartCustomized'
import type { EChartsOption } from 'echarts'

export interface PieChartDataItem {
    name: string
    value: number
    color?: string
    [key: string]: unknown
}

export interface PieChartProps {
    /** Data items to display in the chart */
    data: PieChartDataItem[]
    /** Height of the chart container */
    height?: number
    /** Title of the chart */
    title?: string
    /** Subtitle of the chart */
    subtitle?: string
    /** Whether to show the legend */
    showLegend?: boolean
    /** Legend orientation */
    legendOrient?: 'vertical' | 'horizontal'
    /** Legend position */
    legendPosition?: 'left' | 'right' | 'top' | 'bottom'
    /** Legend icon shape */
    legendIcon?:
        | 'circle'
        | 'rect'
        | 'roundRect'
        | 'triangle'
        | 'diamond'
        | 'pin'
        | 'arrow'
        | 'none'
    /** Legend icon size (width and height in pixels) */
    legendItemSize?: number
    /** Legend type: 'scroll' for single row with pagination, 'plain' for natural wrapping into multiple rows */
    legendType?: 'scroll' | 'plain'
    /** Gap between the chart and legend in pixels */
    legendGap?: number
    /** Whether to show tooltip on hover */
    showTooltip?: boolean
    /** Custom tooltip formatter function. Receives params and returns HTML string */
    tooltipFormatter?: (params: {
        name: string
        value: number
        percent: number
        color: string
        data: PieChartDataItem
    }) => string
    /** Whether to show labels on pie slices */
    showLabel?: boolean
    /** Custom label formatter. Can be a string template or function */
    labelFormatter?:
        | string
        | ((params: {
              name: string
              value: number
              percent: number
              color: string
              data: PieChartDataItem
          }) => string)
    /** Rich text styles for label (use with {styleName|text} syntax in formatter) */
    labelRich?: Record<string, unknown>
    /** Pie chart radius (can be percentage or number) */
    radius?: string | number | [string | number, string | number]
    /** Center position of the pie chart */
    center?: [string | number, string | number]
    /** Custom class name for the container */
    className?: string
}

const PieChart: React.FC<PieChartProps> = ({
    data,
    height = 300,
    title,
    subtitle,
    showLegend = true,
    legendOrient = 'horizontal',
    legendPosition = 'left',
    legendIcon = 'circle',
    legendItemSize = 10,
    legendType = 'scroll',
    legendGap = 0,
    showTooltip = true,
    tooltipFormatter,
    showLabel = true,
    labelFormatter,
    labelRich,
    radius = '50%',
    center = ['50%', '50%'],
    className = '',
}) => {
    // Map legend position to ECharts config
    const getLegendPositionConfig = () => {
        switch (legendPosition) {
            case 'left':
                return { left: legendGap, top: 'center' }
            case 'right':
                return { right: legendGap, top: 'center' }
            case 'top':
                return { top: legendGap, left: 'center' }
            case 'bottom':
                return { bottom: legendGap, left: 'center' }
            default:
                return { left: legendGap, top: 'center' }
        }
    }

    // Build ECharts options
    const options: EChartsOption = {
        title: title
            ? {
                  text: title,
                  subtext: subtitle,
                  left: 'center',
              }
            : undefined,
        tooltip: showTooltip
            ? {
                  trigger: 'item',
                  formatter: tooltipFormatter
                      ? (params: unknown) => {
                            const p = params as {
                                name: string
                                value: number
                                percent: number
                                color: string
                                data: { name: string; value: number }
                            }
                            const originalData = data.find(
                                (d) => d.name === p.name
                            )
                            return tooltipFormatter({
                                name: p.name,
                                value: p.value,
                                percent: p.percent,
                                color: p.color,
                                data: originalData || {
                                    name: p.name,
                                    value: p.value,
                                },
                            })
                        }
                      : '{b}: {c} ({d}%)',
              }
            : undefined,
        legend: showLegend
            ? {
                  type: legendType,
                  orient: legendOrient,
                  icon: legendIcon,
                  itemWidth: legendItemSize,
                  itemHeight: legendItemSize,
                  ...getLegendPositionConfig(),
                  ...(legendPosition === 'bottom' || legendPosition === 'top'
                      ? { width: '80%' }
                      : {}),
              }
            : undefined,
        series: [
            {
                type: 'pie',
                radius: radius,
                center: center,
                data: data.map((item) => ({
                    name: item.name,
                    value: item.value,
                    itemStyle: item.color ? { color: item.color } : undefined,
                })),
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)',
                    },
                },
                label: {
                    show: showLabel,
                    formatter:
                        typeof labelFormatter === 'function'
                            ? (params: unknown) => {
                                  const p = params as {
                                      name: string
                                      value: number
                                      percent: number
                                      color: string
                                      data: { name: string; value: number }
                                  }
                                  const originalData = data.find(
                                      (d) => d.name === p.name
                                  )
                                  return labelFormatter({
                                      name: p.name,
                                      value: p.value,
                                      percent: p.percent,
                                      color: p.color,
                                      data: originalData || {
                                          name: p.name,
                                          value: p.value,
                                      },
                                  })
                              }
                            : labelFormatter || '{b}',
                    rich: labelRich as Record<
                        string,
                        {
                            color?: string
                            fontSize?: number
                            fontWeight?:
                                | 'normal'
                                | 'bold'
                                | 'bolder'
                                | 'lighter'
                                | number
                        }
                    >,
                },
            },
        ],
    }

    return (
        <div className={className}>
            <EChartCustomized
                options={options}
                style={{ height, width: '100%' }}
                opts={{ renderer: 'svg' }}
            />
        </div>
    )
}

export default PieChart
